// Typed wrappers around the /api/v1/macro/* endpoints.
//
// The backend mounts the macro router at ``/api/v1/macro`` and gates
// every endpoint with ``Depends(get_current_user)`` (see
// ``backend/main.py``). The ``apiClient`` baseURL already includes the
// ``/api/v1`` versioning segment (see ``api/client.ts``), so the paths
// below are written WITHOUT the prefix — passing the full path would
// double it (``/api/v1/api/v1/macro/...``) and 404.
//
// Returning ``null`` on 404 lets the SPA's macro widgets render an
// "ingest still warming up" empty state instead of a hard error: the
// 404 from the backend is its way of signalling "no data yet", not "the
// route is wrong". Every other error (401, 5xx) is still thrown.

import { AxiosError } from 'axios';

import { apiClient } from './client';

// --- Response shapes -----------------------------------------------------

export interface MacroRegimeProbabilities {
    expansion: number;
    slowdown: number;
    contraction: number;
    recovery: number;
}

export interface MacroRegimeIndicators {
    g_score: number | null;
    mg_score: number | null;
    i_score: number | null;
    inflation_z: number | null;
}

/**
 * Output of the AFML meta-classifier (Step 6.4 of the macro pipeline).
 *
 * The columns are nullable on ``macro_regimes_m`` — until the worker
 * has produced its first transition prediction both ``transition_probability``
 * and ``is_transition_imminent`` are ``null``. ``horizon_months`` is the
 * forecast horizon the model was trained against (currently 3 months).
 */
export interface AfmlPrediction {
    transition_probability: number | null;
    is_transition_imminent: boolean | null;
    horizon_months: number;
}

/**
 * Derived AFML forecast window computed server-side from the regime
 * month-end ``date`` + ``horizon_months`` so the SPA never re-implements
 * month math. Both labels are pre-formatted ``"%b %Y"`` strings, e.g.
 * ``{ start_label: "Jul 2026", end_label: "Sep 2026" }``. Nullable when
 * the backend has no anchor date to compute from.
 */
export interface ForecastWindow {
    start_label: string;
    end_label: string;
}

export interface MacroRegimeState {
    /** ISO date string (YYYY-MM-DD) of the regime month-end. */
    date: string;
    /**
     * ISO-8601 UTC timestamp of the most recent write to the underlying
     * regime row ("last refreshed"). ``null`` on rows persisted before
     * the provenance column existed — the SPA falls back to ``date``.
     */
    last_updated_utc?: string | null;
    /**
     * EFFECTIVE regime (argmax of the ensemble probabilities): one of
     * "Expansion" | "Slowdown" | "Contraction" | "Recovery". This is the
     * single source of truth shared by the Macro Compass headline, the
     * Cycle Stability Forecast card, and the Tilted Allocation card — it
     * is NOT the sticky, rule-based label (that lives in
     * ``rule_based_regime_label`` for audit only).
     */
    regime_label: string;
    /**
     * Raw rule-based label the ensemble persisted (post-stickiness
     * filter). Provenance/audit only — no UI surface renders it.
     * Optional because older deploys predate the field.
     */
    rule_based_regime_label?: string;
    /** Ensemble confidence in [0, 1]. */
    confidence: number;
    /**
     * Pre-computed AFML forecast window ({start_label, end_label}).
     * Optional because older deploys predate the field.
     */
    forecast_window?: ForecastWindow | null;
    probabilities: MacroRegimeProbabilities;
    indicators: MacroRegimeIndicators;
    /**
     * AFML meta-classifier output. Optional because older deploys
     * predate Step 6.4; treat a missing object the same as ``null``
     * predictions (i.e. hide the Regime Alert widget).
     */
    afml_prediction?: AfmlPrediction | null;
}

export interface MacroTrajectoryPoint {
    date: string;
    /** EFFECTIVE (argmax) regime label for this month. */
    regime_label: string;
    /** Raw rule-based label (provenance only). */
    rule_based_regime_label?: string;
    p_expansion: number;
    p_slowdown: number;
    p_contraction: number;
    p_recovery: number;
}

export interface RegimeAllocation {
    current_regime: string;
    /** Asset-class → weight in percent (sums to 100). */
    baseline_allocation: Record<string, number>;
    tilted_allocation: Record<string, number>;
    tilt_rationale: string;
}

// --- Client --------------------------------------------------------------

export const macroApi = {
    /**
     * Latest regime classification + supporting indicators.
     * Returns ``null`` when the backend has no data yet (404).
     */
    async getCurrentRegime(): Promise<MacroRegimeState | null> {
        try {
            const { data } = await apiClient.get<MacroRegimeState>(
                '/macro/current-regime',
            );
            return data;
        } catch (err) {
            if (err instanceof AxiosError && err.response?.status === 404) {
                return null;
            }
            throw err;
        }
    },

    /**
     * Trailing regime history, oldest → newest.
     * ``months`` is clamped server-side to [1, 240]; defaults to 12.
     */
    async getTrajectory(months = 12): Promise<MacroTrajectoryPoint[]> {
        const { data } = await apiClient.get<MacroTrajectoryPoint[]>(
            '/macro/trajectory',
            { params: { months } },
        );
        return data;
    },

    /**
     * Baseline vs. regime-tilted allocation comparison.
     * Returns ``null`` when there is no regime data yet (404).
     */
    async getRegimeAllocation(): Promise<RegimeAllocation | null> {
        try {
            const { data } = await apiClient.get<RegimeAllocation>(
                '/macro/regime-allocation',
            );
            return data;
        } catch (err) {
            if (err instanceof AxiosError && err.response?.status === 404) {
                return null;
            }
            throw err;
        }
    },
};

/**
 * Resolve the EFFECTIVE current regime from a regime state: the label
 * with the highest ensemble probability. This is the single frontend
 * source of truth for "which regime is live right now?" and mirrors the
 * Macro Compass headline reducer EXACTLY — iteration order
 * ``[Expansion, Recovery, Slowdown, Contraction]`` with the first
 * maximum winning on a tie — as well as the backend
 * ``app/domain/macro_regime/regime_selection.py::dominant_regime``.
 *
 * Using the dominant probability (rather than the sticky, rule-based
 * ``regime_label``) keeps every surface — the Macro Compass, the
 * Robo-Advisor card, and the strategy tear-sheet "Regime:" badge — in
 * lock-step. Returns ``null`` when no state is available so the caller
 * can fall back to a stored label.
 */
export function dominantRegimeLabel(
    state: MacroRegimeState | null | undefined,
): string | null {
    if (!state) return null;
    const ordered: readonly (readonly [string, number])[] = [
        ['Expansion', state.probabilities.expansion],
        ['Recovery', state.probabilities.recovery],
        ['Slowdown', state.probabilities.slowdown],
        ['Contraction', state.probabilities.contraction],
    ];
    const [label] = ordered.reduce((best, curr) =>
        curr[1] > best[1] ? curr : best,
    );
    return label;
}

// --- Formatting helpers --------------------------------------------------

/**
 * Format a month-end ISO date (``YYYY-MM-DD``) as a human "Month YYYY"
 * label, e.g. ``"2026-08-31" -> "August 2026"``.
 *
 * The regime panel is anchored to calendar month-ends, so the raw ISO
 * date reads as an oddly-specific (and, for the current in-progress
 * month, future-dated) day. Collapsing it to the month + year matches
 * how users think about a monthly macro reading and avoids the
 * "why does it say the 31st?" confusion. Components parse the ISO
 * components directly rather than ``new Date(iso)`` to sidestep older
 * Safari's ambiguous-string parsing.
 */
export function formatMonthEnd(iso: string | null | undefined): string {
    if (!iso) return '—';
    const [yearStr, monthStr] = iso.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    if (!year || !month) return iso;
    const monthName = new Date(year, month - 1, 1).toLocaleString(undefined, {
        month: 'long',
    });
    return `${monthName} ${year}`;
}

export default macroApi;
